import * as React from 'react'
import { Funnel } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'
import { Card, CardHeader, CardTitle, CardAction, CardContent } from '../core/card'
import { type PeriodValue } from '../core/period-picker'
import {
  PipelineBar,
  PerformanceControls,
  type PipelineStageDatum,
} from './performance-charts'

/**
 * PipelineWidget — "Pipeline by stage" as a standalone card: a stacked
 * proportion bar split by stage over a two-column legend (stage · count). Stage
 * colors should be each stage's own badge color (the shared stage palette), so
 * this reads the same as the deal pipeline elsewhere. Hover a segment for its
 * count. Height fits the content.
 *
 *   <PipelineWidget pipeline={pipeline} onPeriodApply={reload} />
 */

export interface PipelineWidgetProps {
  pipeline: PipelineStageDatum[]
  periodLabel?: React.ReactNode
  defaultPeriod?: PeriodValue
  onPeriodApply?: (value: PeriodValue) => void
  /** Wire the ⋯ menu's "Hide widget" action; hidden when omitted. */
  onHide?: () => void
  title?: React.ReactNode
  className?: string
}

export const PipelineWidget = React.forwardRef<
  HTMLDivElement,
  PipelineWidgetProps
>(
  (
    { pipeline, periodLabel = 'Period', defaultPeriod, onPeriodApply, onHide, title = 'Pipeline by stage', className },
    ref
  ) => (
    <Card ref={ref} className={cn('w-full max-w-xl', className)}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Funnel aria-hidden="true" className="size-4 text-fbui-gray-500" />
          {title}
        </CardTitle>
        <CardAction>
          <PerformanceControls
            periodLabel={periodLabel}
            defaultPeriod={defaultPeriod}
            onPeriodApply={onPeriodApply}
            onHide={onHide}
            hideLabel="Pipeline options"
          />
        </CardAction>
      </CardHeader>
      <CardContent>
        <PipelineBar pipeline={pipeline} />
      </CardContent>
    </Card>
  )
)
PipelineWidget.displayName = 'PipelineWidget'
